@if (isAuthenticated) {
    <app-version-nav></app-version-nav>
  } @else {
    <app-header></app-header>
  }
<div class="pricing-page">
  <div class="pricing-container">
    <div class="pricing-header">
      <h1>Pricing and Custom Calculator</h1>
      <p class="subtitle">Simple base pricing plus custom estimation for advanced requirements</p>

      <div class="billing-toggle">
        <span [class.active]="!isAnnual">Monthly</span>
        <mat-slide-toggle [checked]="isAnnual" (change)="onToggleChange($event)"></mat-slide-toggle>
        <span [class.active]="isAnnual">Annual</span>
        @if (isAnnual) {
          <span class="save-badge">1 month free</span>
        }
      </div>
    </div>

    <div class="segment-switch">
      @for (segment of userTypes; track segment.value) {
        <button
          type="button"
          class="segment-button"
          [class.active]="selectedUserType === segment.value"
          (click)="setUserType(segment.value)">
          {{ segment.label }}
        </button>
      }
    </div>

    <section class="top-sections">
      <mat-card class="static-pricing-card">
        <mat-card-header>
          <mat-card-title>Standard {{ selectedUserType === 'agency' ? 'Agency' : 'Brand' }} Plan</mat-card-title>
          <mat-card-subtitle>Best for predictable monthly usage</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div class="price-line">
            <span class="currency">£</span>
            <span class="amount">{{ staticBasePrice.toLocaleString() }}</span>
            <span class="period">/ {{ getBillingPeriod() }}</span>
          </div>
          <p class="annual-note">Annual billing includes one month free equivalent.</p>
          <ul class="included-list">
            @for (item of staticPlanHighlights; track item) {
              <li>
                <mat-icon>check_circle</mat-icon>
                <span>{{ item }}</span>
              </li>
            }
          </ul>
          <button mat-raised-button class="primary-cta" (click)="selectPlan()">Start with Standard Plan</button>
        </mat-card-content>
      </mat-card>
    </section>

    <section class="calculator-section">
      <div class="calculator-left">
        <h2>Pricing Calculator</h2>
        <p class="helper-text">Use this for custom pricing estimates. Consultancy is quoted separately.</p>

        <div class="field">
          <label>{{ selectedUserType === 'agency' ? 'Clients' : 'Projects' }}</label>
          <div class="inline-input">
            <input
              type="number"
              [value]="projectsCount"
              min="1"
              max="50"
              (input)="onProjectsInput($any($event.target).value)" />
            <small>1 - 50</small>
          </div>
          <input
            type="range"
            min="1"
            max="50"
            [value]="projectsCount"
            (input)="onProjectsInput($any($event.target).value)" />
        </div>

        <div class="field">
          <label>Prompt volume / month</label>
          <div class="inline-input">
            <input
              type="number"
              [value]="promptUsage"
              min="100"
              max="5000"
              step="50"
              (input)="onPromptInput($any($event.target).value)" />
            <small>100 - 5000</small>
          </div>
          <input
            type="range"
            min="100"
            max="5000"
            step="50"
            [value]="promptUsage"
            (input)="onPromptInput($any($event.target).value)" />
        </div>

        <div class="field">
          <label>Feature add-ons</label>
          <small class="addon-intent-note">Select what you are interested in. We will contact you with custom pricing later.</small>
          <div class="addon-checklist">
            @for (feature of optionalFeatures; track feature.key) {
              <label class="addon-item">
                <input
                  type="checkbox"
                  [checked]="isFeatureSelected(feature.key)"
                  (change)="toggleFeature(feature.key)" />
                <span>{{ feature.label }}</span>
                <em>(Coming soon)</em>
              </label>
            }
          </div>
        </div>

        <div class="field">
          <label>Extra users</label>
          <input
            type="number"
            min="0"
            max="1000"
            [value]="extraUsers"
            (input)="onExtraUsersInput($any($event.target).value)" />
          <small class="p-l-1">£3 per user / month</small>
        </div>
      </div>
      <div class="calculator-right">
        <h3>Custom Pricing Estimate</h3>
        <div class="result-grid">
          <div>
            <span class="label">Billing</span>
            <strong>{{ isAnnual ? 'Annual' : 'Monthly' }}</strong>
          </div>
          <div>
            <span class="label">Base package</span>
            <strong>£{{ calculatorBreakdown.base.toLocaleString() }}</strong>
          </div>
          <div>
            <span class="label">Add-ons</span>
            <strong>£{{ calculatorBreakdown.addons.toLocaleString() }}</strong>
          </div>
        </div>

        <div class="total-block">
          <span>Total estimate</span>
          <h4>£{{ calculatorBreakdown.total.toLocaleString() }}</h4>
          <p>per {{ getBillingPeriod() }}</p>
        </div>

        <div class="result-notes">
          <p><strong>Cost components:</strong> Base price + usage overage + extra users.</p>
          <p><strong>Note:</strong> Activation and consultancy are scoped separately via contact.</p>
        </div>

        <button mat-raised-button class="cta-button" (click)="selectPlan()">
          Request custom quote
        </button>
        <p class="consult-note">Consultancy and activation services are scoped after discovery call.</p>
      </div>
    </section>

    <section class="contact-row">
      <div class="contact-card">
        <h3>Contact Us For Custom Pricing</h3>
        <p>Need consultancy, activation support, or enterprise rollout? Talk to us directly.</p>
        <div class="contact-links">
          <a href="tel:+441234567890">Call: +44 1234 567 890</a>
          <a href="mailto:hello%40brandscore.ai">Email: hello&#64;brandscore.ai</a>
          <a href="https://calendly.com" target="_blank" rel="noreferrer">Book a calendar slot</a>
        </div>
        <form class="contact-form">
          <input type="text" placeholder="Your name" />
          <input type="email" placeholder="Work email" />
          <textarea rows="3" placeholder="Tell us your requirements"></textarea>
          <button type="button">Contact us</button>
        </form>
      </div>
    </section>

    <section class="compare-section">
      <h2>Compare Standard vs Custom</h2>
      <p class="compare-subtitle">A quick view of what is included today and what can be expanded through custom pricing.</p>
      <div class="compare-table-wrap">
        <table class="compare-table">
          <thead>
            <tr>
              <th>Capability</th>
              <th>Standard Plan</th>
              <th>Custom Pricing</th>
            </tr>
          </thead>
          <tbody>
            @for (row of capabilityRows; track row.feature) {
              <tr>
                <td>{{ row.feature }}</td>
                <td>{{ row.standard }}</td>
                <td>{{ row.custom }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>

    <section class="testimonials-section">
      <h2>Testimonials</h2>
      <div class="testimonial-grid">
        @for (item of testimonials; track item.name) {
          <article>
            <p class="quote">"{{ item.quote }}"</p>
            <div class="person">
              <img [src]="item.avatarUrl" [alt]="item.name" loading="lazy" />
              <div>
                <p class="author">{{ item.name }}</p>
                <p class="role">{{ item.role }}</p>
              </div>
            </div>
          </article>
        }
      </div>
    </section>

    <section class="trust-strip">
      <article>
        <h4>Built for scale</h4>
        <p>Start simple, then expand prompts, audits, and reporting as your AI visibility program grows.</p>
      </article>
      <article>
        <h4>Security-first operations</h4>
        <p>Role-based access, encrypted data flows, and structured governance for team-level adoption.</p>
      </article>
      <article>
        <h4>Outcome-driven support</h4>
        <p>From onboarding to strategy reviews, we help convert AI search visibility into measurable growth.</p>
      </article>
    </section>

    <section class="faq-section">
      <h2>FAQ</h2>
      <div class="faq-list">
        @for (faq of faqItems; track faq.q) {
          <div class="faq-item">
            <h4>{{ faq.q }}</h4>
            <p>{{ faq.a }}</p>
          </div>
        }
      </div>
    </section>

    <section class="policy-grid">
      <article>
        <h3>Privacy Policy</h3>
        <p>We process usage and account data strictly for service delivery, analytics, and support. Data handling follows contractual and regulatory obligations.</p>
      </article>
      <article>
        <h3>Terms of Service</h3>
        <p>Access and usage are governed by active subscription terms, fair-use policies, and data-processing agreements defined in your contract.</p>
      </article>
      <article>
        <h3>Cookie Policy</h3>
        <p>We use essential and analytics cookies to improve performance, reliability, and product experience. You can manage preferences in browser settings.</p>
      </article>
      <article>
        <h3>Security</h3>
        <p>Our platform uses encrypted transport, secure access controls, and continuous monitoring practices to protect customer and operational data.</p>
      </article>
    </section>
  </div>
</div>
